/**
 * @file chat-format.ts
 * @description Подписи времени, имён и номеров окна «Сообщения»
 * (chat-window-design.md §2, §3; SC58 — время по поясу магазина).
 *
 * Чистые функции: «сейчас» передаётся параметром, зона — из `shop-time`
 * (её ставит каркас кабинета). Тестируются без React.
 */

import { getShopTimeZone } from "../../lib/shop-time";
import { CHAT_TEXT } from "./chat-texts";

/** Части даты в поясе магазина. */
function parts(iso: string, zone: string): { y: string; m: string; d: string; hh: string; mm: string } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const map: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat("ru-RU", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)) {
    map[p.type] = p.value;
  }
  return { y: map.year ?? "", m: map.month ?? "", d: map.day ?? "", hh: map.hour ?? "", mm: map.minute ?? "" };
}

/** Ключ дня «2026-10-02» в поясе магазина — для разделителей ленты. */
export function dayKey(iso: string, zone: string = getShopTimeZone()): string {
  const p = parts(iso, zone);
  return p ? `${p.y}-${p.m}-${p.d}` : "";
}

/** Ключ вчерашнего дня относительно `now`. */
function yesterdayKey(now: Date, zone: string): string {
  return dayKey(new Date(now.getTime() - 86_400_000).toISOString(), zone);
}

/** Время «14:32». */
export function clockLabel(iso: string, zone: string = getShopTimeZone()): string {
  const p = parts(iso, zone);
  return p ? `${p.hh}:${p.mm}` : "";
}

/** Время в строке списка: «14:32» / «вчера» / «20.09» / «20.09.25». */
export function rowTimeLabel(iso: string | null, now: Date = new Date(), zone: string = getShopTimeZone()): string {
  if (!iso) return "";
  const p = parts(iso, zone);
  const today = parts(now.toISOString(), zone);
  if (!p || !today) return "";
  const key = `${p.y}-${p.m}-${p.d}`;
  if (key === `${today.y}-${today.m}-${today.d}`) return `${p.hh}:${p.mm}`;
  if (key === yesterdayKey(now, zone)) return CHAT_TEXT.yesterday;
  if (p.y === today.y) return `${p.d}.${p.m}`;
  return `${p.d}.${p.m}.${p.y.slice(2)}`;
}

const MONTHS_GENITIVE = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

/** Разделитель дня в ленте: «Сегодня» / «Вчера» / «28 сентября» / «28 сентября 2025». */
export function dayTitle(iso: string, now: Date = new Date(), zone: string = getShopTimeZone()): string {
  const p = parts(iso, zone);
  const today = parts(now.toISOString(), zone);
  if (!p || !today) return "";
  const key = `${p.y}-${p.m}-${p.d}`;
  if (key === `${today.y}-${today.m}-${today.d}`) return CHAT_TEXT.today;
  if (key === yesterdayKey(now, zone)) return CHAT_TEXT.yesterdayTitle;
  const month = MONTHS_GENITIVE[Number(p.m) - 1] ?? "";
  const base = `${Number(p.d)} ${month}`;
  return p.y === today.y ? base : `${base} ${p.y}`;
}

/** «сегодня в 14:31» / «вчера в 14:31» / «28.09 в 14:31» — полоса «Диалог передан вам». */
export function whenPhrase(iso: string, now: Date = new Date(), zone: string = getShopTimeZone()): string {
  const time = clockLabel(iso, zone);
  const title = dayTitle(iso, now, zone);
  if (title === CHAT_TEXT.today) return `сегодня в ${time}`;
  if (title === CHAT_TEXT.yesterdayTitle) return `вчера в ${time}`;
  const p = parts(iso, zone);
  return p ? `${p.d}.${p.m} в ${time}` : time;
}

/** Полная дата для раскрытой служебной записи: «02.10.2026, 14:31». */
export function fullDateTime(iso: string, zone: string = getShopTimeZone()): string {
  const p = parts(iso, zone);
  return p ? `${p.d}.${p.m}.${p.y}, ${p.hh}:${p.mm}` : "";
}

/** Слова имени без пустых. */
function words(name: string): string[] {
  return name.trim().split(/\s+/).filter(Boolean);
}

/** «Анна Кравец» → «Анна К.» — автор коллеги перед последним сообщением. */
export function nameWithInitial(name: string): string {
  const [first, last] = words(name);
  if (!first) return "";
  return last ? `${first} ${last.charAt(0)}.` : first;
}

/** «Анна Кравец» → «А. Кравец» — ведущий, когда строке тесно. */
export function initialWithSurname(name: string): string {
  const [first, last] = words(name);
  if (!first) return "";
  return last ? `${first.charAt(0)}. ${last}` : first;
}

/** Инициалы для кружка: «Пётр Смирнов» → «ПС». */
export function initials(name: string): string {
  const w = words(name);
  return w
    .slice(0, 2)
    .map((x) => x.charAt(0).toUpperCase())
    .join("") || "?";
}

/** «Z-2026-000006» → «Z-…000006»: хвост номера различает заказы. */
export function compactOrderNumber(number: string): string {
  const tail = number.split("-").pop() ?? number;
  return tail.length < number.length ? `${number.charAt(0)}-…${tail}` : number;
}

/** Имя клиента для списка и шапки. */
export function customerName(customer: { displayName: string | null; email: string | null }): string {
  return customer.displayName?.trim() || customer.email || "Клиент";
}

/** Сумма заказа «1 175,00 ₽». */
export function moneyLabel(value: string, currency: string): string {
  const num = Number(value);
  const text = Number.isNaN(num)
    ? value
    : num.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === "RUB" ? `${text} ₽` : `${text} ${currency}`;
}
